/**
 * Op stack (Blender modifier panel) editing for a part's `ops`: the add menu
 * catalog, viewport / export flags, reorder, duplicate, remove, and the
 * remembered collapse state of each op card. Pure except the storage helpers.
 */

import { OP_DEFAULTS, type ModelOp } from './model-doc.js';

/** Defaults of ops the model format has but `OP_DEFAULTS` does not list. */
const EXTRA_OP_DEFAULTS: Record<string, Record<string, unknown>> = {
  fur: { material: 'fur', length: 0.02, shells: 12, gravity: 0.3, comb: 0.6, tile: 0.04, detail: 1, wind: 1 },
  sculpt: { strokes: [] },
};

/** Default fields of an op type (without `op`). */
export function opDefaults(name: string): Record<string, unknown> {
  return OP_DEFAULTS[name] ?? EXTRA_OP_DEFAULTS[name] ?? {};
}

export interface OpGroup {
  label: string;
  ops: string[];
}

/** The "Add modifier" menu, grouped like Blender's (unknown ops land in Other). */
const GROUPS: OpGroup[] = [
  { label: 'Generate', ops: ['subsurf', 'subdivide', 'mirror', 'bevel', 'boolean', 'remesh', 'decimate', 'unwrap'] },
  { label: 'Deform', ops: ['twist', 'taper', 'bend', 'smooth', 'inflate', 'noise', 'sculpt'] },
  { label: 'Color', ops: ['vertexColor', 'flatShade'] },
  { label: 'Fur', ops: ['fur'] },
];

/** Op types offered by the add menu, grouped; only types the editor knows defaults for. */
export function opGroups(): OpGroup[] {
  const known = new Set([...Object.keys(OP_DEFAULTS), ...Object.keys(EXTRA_OP_DEFAULTS)]);
  const listed = new Set(GROUPS.flatMap((g) => g.ops));
  const groups = GROUPS.map((g) => ({ label: g.label, ops: g.ops.filter((op) => known.has(op)) }));
  const other = [...known].filter((op) => !listed.has(op));
  if (other.length) groups.push({ label: 'Other', ops: other });
  return groups.filter((g) => g.ops.length > 0);
}

/** "vertexColor" → "Vertex Color", "subsurf" → "Subdivision Surface". */
export function opLabel(name: string): string {
  const special: Record<string, string> = { subsurf: 'Subdivision Surface', flatShade: 'Flat Shade', vertexColor: 'Vertex Color' };
  return special[name] ?? name.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

/** A new op of that type with its default fields. */
export function newOp(name: string): ModelOp {
  return { op: name, ...structuredCloneJson(opDefaults(name)) };
}

function structuredCloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function addOp(ops: readonly ModelOp[], name: string): ModelOp[] {
  return [...ops, newOp(name)];
}

export type OpFlag = 'viewport' | 'export';

/** True when the op runs in the viewport / on export (both default on). */
export function opFlag(op: ModelOp, flag: OpFlag): boolean {
  return op[flag] !== false;
}

/** Switch `viewport` / `export`; on (the default) removes the field. */
export function setOpFlag(op: ModelOp, flag: OpFlag, on: boolean): ModelOp {
  const next: ModelOp = { ...op };
  if (on) delete next[flag];
  else next[flag] = false;
  return next;
}

export function setOpFlagAt(ops: readonly ModelOp[], index: number, flag: OpFlag, on: boolean): ModelOp[] {
  return ops.map((op, i) => (i === index ? setOpFlag(op, flag, on) : op));
}

export function removeOp(ops: readonly ModelOp[], index: number): ModelOp[] {
  return ops.filter((_, i) => i !== index);
}

/** Copy the op right after itself. */
export function duplicateOp(ops: readonly ModelOp[], index: number): ModelOp[] {
  const op = ops[index];
  if (!op) return ops.slice();
  const next = ops.slice();
  next.splice(index + 1, 0, structuredCloneJson(op));
  return next;
}

/**
 * Drag-and-drop reorder: move op `from` so it lands in the gap `gap`
 * (0 = top, ops.length = bottom), the gap index counted before the move.
 */
export function reorderOp(ops: readonly ModelOp[], from: number, gap: number): ModelOp[] {
  if (from < 0 || from >= ops.length) return ops.slice();
  const to = gap > from ? gap - 1 : gap;
  if (to === from) return ops.slice();
  const next = ops.slice();
  const [moved] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(next.length, to)), 0, moved!);
  return next;
}

/** Where the op at old index `i` ends up after `reorderOp(ops, from, gap)`. */
export function reorderedIndex(i: number, from: number, gap: number): number {
  const to = gap > from ? gap - 1 : gap;
  if (i === from) return to;
  if (from < to && i > from && i <= to) return i - 1;
  if (to < from && i >= to && i < from) return i + 1;
  return i;
}

/** Ops 0..=index baked by Apply on the card at `index` (the stack evaluates top-down). */
export function applyCount(index: number): number {
  return index + 1;
}

/* ------------------------------------------------------------------ */
/* Collapse state (per model + part + op index, kept in localStorage)  */

const COLLAPSE_KEY = 'prism.model.opCollapsed';

export function collapseKey(model: string | null, part: string, index: number): string {
  return `${model ?? ''}::${part}::${index}`;
}

export function readCollapsed(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? '[]') as unknown;
    return new Set(Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

export function writeCollapsed(set: ReadonlySet<string>): void {
  try {
    // Keep the newest entries; old models' keys fall off.
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...set].slice(-500)));
  } catch {
    // Storage unavailable: collapse state lasts for this session.
  }
}

/**
 * Collapse keys of one part after its ops were re-indexed: `map(oldIndex)`
 * gives the new index, or null when that op was removed.
 */
export function remapCollapsed(
  set: ReadonlySet<string>,
  model: string | null,
  part: string,
  count: number,
  map: (index: number) => number | null,
): Set<string> {
  const next = new Set(set);
  const was: number[] = [];
  for (let i = 0; i < count + 1; i += 1) {
    if (next.delete(collapseKey(model, part, i))) was.push(i);
  }
  for (const i of was) {
    const j = map(i);
    if (j !== null) next.add(collapseKey(model, part, j));
  }
  return next;
}
